import { useEffect, useState } from 'react';
import './ToastContainer.css';

const ICON_MAP = {
  success: '✓',
  error:   '✕',
  warning: '!',
  info:    'i',
};

/**
 * 把所有 Toast 訊息渲染在畫面右下角。
 * 由 ToastProvider 注入 toasts 與 onDismiss。
 */
function ToastContainer({ toasts, onDismiss }) {
  return (
    <div className="toast-container" role="region" aria-label="通知區">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

/**
 * 單一 Toast 訊息。
 *
 * 動畫處理：
 *   - 進場：CSS animation toast-in，掛載即播放
 *   - 自動消失：duration 將盡時 setLeaving(true) 觸發退場動畫，然後實際移除
 *   - 手動關閉：點 X → setLeaving(true) → 200ms 後 onDismiss
 *
 * 為什麼要 setLeaving 而不是直接 onDismiss？
 *   onDismiss 會立刻 unmount，看不到退場動畫。
 *   先切 leaving 狀態跑完動畫，再 unmount。
 */
function ToastItem({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false);
  const ANIM_OUT_MS = 220;

  useEffect(() => {
    if (toast.duration <= 0) return undefined;
    const enterTimer = setTimeout(() => setLeaving(true), toast.duration - ANIM_OUT_MS);
    const exitTimer  = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
    };
  }, [toast, onDismiss]);

  const handleClose = () => {
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), ANIM_OUT_MS);
  };

  return (
    <div
      className={`toast toast-${toast.type}${leaving ? ' leaving' : ''}`}
      role={toast.type === 'error' ? 'alert' : 'status'}
    >
      <span className="toast-icon" aria-hidden="true">{ICON_MAP[toast.type] || 'i'}</span>
      <span className="toast-message">{toast.message}</span>
      <button className="toast-close" onClick={handleClose} aria-label="關閉通知">×</button>
    </div>
  );
}

export default ToastContainer;
